/**
 * AI Agent API console — TEMPORARY.
 *
 * A window onto the Lusid AI Agent external workflow API, which will
 * eventually turn Chapter 99 raw legal text into rule-engine pseudocode.
 * Nothing here is wired to the rule engine yet; this is for seeing exactly
 * what goes out and what comes back.
 *
 * The API credentials live in be-core and never reach this page. The form is
 * built from the server's endpoint catalog, so the browser names an endpoint
 * rather than composing a URL.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownward';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';

import TopBar from '../../../../../components/utils/TopBar';
import * as api from '../../../../../api/index';
import { toast } from 'react-toastify';

const C = {
  bg:      '#0f1117',
  panel:   '#1a1b23',
  line:    '#1e293b',
  border:  '#334155',
  text:    '#e2e8f0',
  muted:   '#64748b',
  dim:     '#94a3b8',
  blue:    '#60a5fa',
  green:   '#4ade80',
  amber:   '#fbbf24',
  red:     '#f87171',
};

const inputSx = {
  '& .MuiInputBase-root': { color: C.text, background: '#121620', fontSize: 13 },
  '& .MuiInputLabel-root': { color: C.muted, fontSize: 13 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '& .MuiFormHelperText-root': { color: C.muted, fontSize: 11, marginLeft: 0 },
};

const Panel = ({ title, icon, right, children, accent }) => (
  <Box sx={{
    background: C.panel, border: `1px solid ${C.line}`, borderRadius: '8px',
    overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0,
  }}>
    <Box sx={{
      px: '14px', py: '9px', background: C.bg, borderBottom: `1px solid ${C.line}`,
      display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
    }}>
      {icon}
      <Typography variant="caption" fontWeight={700} color={accent || C.dim}
        sx={{ letterSpacing: '.06em', textTransform: 'uppercase', fontSize: 10 }}>
        {title}
      </Typography>
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>{right}</Box>
    </Box>
    <Box sx={{ p: '14px', overflow: 'auto', flex: 1 }}>{children}</Box>
  </Box>
);

const Json = ({ value, max = 460 }) => (
  <Box component="pre" sx={{
    margin: 0, fontFamily: 'monospace', fontSize: 11.5, lineHeight: 1.65,
    color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    maxHeight: max, overflow: 'auto',
  }}>
    {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
  </Box>
);

const KV = ({ k, v, mono, color }) => (
  <Box sx={{ display: 'flex', gap: '10px', py: '3px', alignItems: 'baseline' }}>
    <Typography variant="caption" color={C.muted} sx={{ fontSize: 11, minWidth: 128, flexShrink: 0 }}>
      {k}
    </Typography>
    <Typography variant="caption" sx={{
      fontSize: 11.5, color: color || C.text, wordBreak: 'break-all',
      fontFamily: mono ? 'monospace' : 'inherit',
    }}>
      {v}
    </Typography>
  </Box>
);

const AiAgentApiPage = () => {
  const [booting, setBooting] = useState(true);
  const [sending, setSending] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [endpoints, setEndpoints] = useState([]);

  const [endpointKey, setEndpointKey] = useState('templates.list');
  const [pathParams, setPathParams] = useState({});
  const [queryParams, setQueryParams] = useState({});
  const [bodyFields, setBodyFields] = useState({});

  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  // Carried between calls so UUIDs don't have to be copied by hand — the
  // whole workflow is "start an execution, then keep talking to it".
  const [context, setContext] = useState({ template_id: '', execution_id: '' });
  const contextRef = useRef(context);
  contextRef.current = context;

  const endpoint = useMemo(
    () => endpoints.find((e) => e.key === endpointKey) || null,
    [endpoints, endpointKey],
  );

  useEffect(() => { loadCatalog(); }, []); // eslint-disable-line

  const loadCatalog = async () => {
    setBooting(true);
    try {
      const { data, status } = await api.getAiAgentCatalogBIZcoreTD();
      if (status === 200 && data?.data) {
        setCredentials(data.data.credentials || null);
        setEndpoints(data.data.endpoints || []);
      } else {
        toast.error('Could not load the endpoint catalog');
      }
    } catch (err) {
      toast.error('Could not reach be-core');
    }
    setBooting(false);
  };

  // Reset the form whenever the endpoint changes, pre-filling any id we hold.
  useEffect(() => {
    if (!endpoint) return;
    const next = {};
    endpoint.path_params.forEach((p) => {
      next[p.name] = contextRef.current[p.name] || '';
    });
    setPathParams(next);
    setQueryParams({});
    const body = {};
    endpoint.body_fields.forEach((f) => {
      body[f.name] = f.name === 'template_id' ? (contextRef.current.template_id || '') : '';
    });
    setBodyFields(body);
  }, [endpointKey, endpoint]);

  const grouped = useMemo(() => {
    const out = [];
    endpoints.forEach((e) => {
      let g = out.find((x) => x.group === e.group);
      if (!g) { g = { group: e.group, items: [] }; out.push(g); }
      g.items.push(e);
    });
    return out;
  }, [endpoints]);

  /** Pull the ids out of a response so the next call can use them. */
  const absorbContext = (json) => {
    const d = json?.data;
    if (!d) return;
    const execId = d.execution?.execution_id || d.execution_id;
    const tplId = d.execution?.template?.id;
    if (execId || tplId) {
      setContext((prev) => ({
        template_id: tplId || prev.template_id,
        execution_id: execId || prev.execution_id,
      }));
    }
  };

  const send = async () => {
    if (!endpoint) return;
    const missing = endpoint.path_params
      .filter((p) => p.required && !(pathParams[p.name] || '').trim())
      .map((p) => p.name)
      .concat(
        endpoint.body_fields
          .filter((f) => f.required && !String(bodyFields[f.name] ?? '').trim())
          .map((f) => f.name),
      );
    if (missing.length) {
      toast.error(`Required: ${missing.join(', ')}`);
      return;
    }

    // Anything declared as JSON is parsed here so a typo is caught before the
    // request leaves, rather than coming back as a 400 from the far end.
    const body = {};
    for (const f of endpoint.body_fields) {
      const raw = bodyFields[f.name];
      if (raw === undefined || raw === null || String(raw).trim() === '') continue;
      if (f.type === 'json') {
        try {
          body[f.name] = JSON.parse(raw);
        } catch (e) {
          toast.error(`"${f.name}" is not valid JSON`);
          return;
        }
      } else if (f.type === 'number') {
        body[f.name] = Number(raw);
      } else {
        body[f.name] = raw;
      }
    }

    setSending(true);
    try {
      const { data, status } = await api.callAiAgentBIZcoreTD({
        endpoint_key: endpoint.key,
        path_params: pathParams,
        query_params: queryParams,
        body,
      });
      if (status === 200 && data?.data) {
        setResult(data.data);
        setHistory((h) => [{
          at: new Date(),
          key: endpoint.key,
          label: endpoint.label,
          method: endpoint.method,
          httpStatus: data.data.response?.http_status ?? null,
          ok: data.data.response?.envelope_success ?? data.data.response?.ok ?? false,
          errorCode: data.data.response?.error_code || data.data.transport_error || null,
          ms: data.data.elapsed_ms,
        }, ...h].slice(0, 25));
        absorbContext(data.data.response?.json);
      } else {
        toast.error(typeof data?.data === 'string' ? data.data : 'Request rejected');
        setResult(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.data || err?.message || 'Request failed';
      toast.error(typeof msg === 'string' ? msg : 'Request failed');
    }
    setSending(false);
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text ?? '');
    toast.info('Copied');
  };

  const res = result?.response;
  const req = result?.request;
  const execution = res?.json?.data?.execution;
  const awaiting = execution?.awaiting_input;
  const replies = res?.json?.data?.replies;

  const statusColor = !res ? C.red
    : res.envelope_success === true ? C.green
    : res.http_status >= 500 ? C.red
    : res.http_status >= 400 ? C.amber
    : C.green;

  if (booting) {
    return (
      <Box>
        <TopBar title={"AI Agent API"} />
        <Box height="300px" className="flexCenterCenterRow"><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <TopBar title={"AI Agent API"} />

      <Box sx={{ background: C.bg, borderRadius: '8px', border: `1px solid ${C.line}`, p: '16px' }}>

        {/* ── Credentials ─────────────────────────────────────────────── */}
        {credentials && !credentials.configured && (
          <Alert severity="error" sx={{ mb: '14px', fontSize: 13 }}>
            Not configured on the server. Missing environment variable(s):{' '}
            <strong>{(credentials.missing || []).join(', ')}</strong>
          </Alert>
        )}

        {credentials?.configured && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap',
            p: '10px 14px', mb: '14px', borderRadius: '8px',
            background: '#111827', border: `1px solid ${C.line}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <LockOutlinedIcon sx={{ fontSize: 15, color: C.green }} />
              <Typography variant="caption" color={C.green} fontWeight={700} sx={{ fontSize: 11 }}>
                Credentials server-side
              </Typography>
            </Box>
            <Typography variant="caption" color={C.muted} sx={{ fontSize: 11, fontFamily: 'monospace' }}>
              {credentials.base_url}
            </Typography>
            <Typography variant="caption" color={C.muted} sx={{ fontSize: 11 }}>
              key <span style={{ fontFamily: 'monospace', color: C.dim }}>{credentials.api_key_masked}</span>
              {' · '}secret <span style={{ color: C.dim }}>held by be-core, never sent here</span>
              {' · '}timeout {credentials.timeout_seconds}s
            </Typography>
          </Box>
        )}

        {/* ── Execution context ───────────────────────────────────────── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          p: '10px 14px', mb: '14px', borderRadius: '8px',
          background: '#121620', border: `1px dashed ${C.border}`,
        }}>
          <Typography variant="caption" color={C.dim} fontWeight={700} sx={{ fontSize: 10, letterSpacing: '.06em' }}>
            CARRIED FORWARD
          </Typography>
          {['template_id', 'execution_id'].map((field) => (
            <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography variant="caption" color={C.muted} sx={{ fontSize: 11 }}>{field}</Typography>
              <TextField
                size="small" variant="standard"
                placeholder="—"
                value={context[field]}
                onChange={(e) => setContext((c) => ({ ...c, [field]: e.target.value }))}
                sx={{
                  width: 300,
                  '& .MuiInputBase-root': { color: C.blue, fontSize: 11, fontFamily: 'monospace' },
                  '& .MuiInput-underline:before': { borderColor: C.border },
                }}
              />
              {context[field] && (
                <Tooltip title="Copy">
                  <IconButton size="small" onClick={() => copy(context[field])} sx={{ p: '2px', color: C.muted }}>
                    <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          <Tooltip title="Clear carried ids">
            <IconButton size="small" onClick={() => setContext({ template_id: '', execution_id: '' })}
              sx={{ color: C.muted, ml: 'auto' }}>
              <RestartAltOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 400px) 1fr', gap: '14px', alignItems: 'start' }}>

          {/* ── Request builder ──────────────────────────────────────── */}
          <Panel title="Build request" accent={C.blue}>
            <TextField
              select fullWidth size="small" label="Endpoint"
              value={endpointKey}
              onChange={(e) => setEndpointKey(e.target.value)}
              sx={{ ...inputSx, mb: '12px' }}
            >
              {grouped.map((g) => ([
                <MenuItem key={g.group} disabled sx={{ fontSize: 10, opacity: 0.7, letterSpacing: '.08em' }}>
                  {g.group.toUpperCase()}
                </MenuItem>,
                ...g.items.map((e) => (
                  <MenuItem key={e.key} value={e.key} sx={{ fontSize: 13, pl: '24px' }}>
                    <Chip label={e.method} size="small" sx={{
                      height: 16, fontSize: 9, fontWeight: 700, mr: '8px',
                      background: 'transparent', color: C.blue, border: `1px solid ${C.border}`,
                      '& .MuiChip-label': { px: '5px' },
                    }} />
                    {e.label}
                  </MenuItem>
                )),
              ]))}
            </TextField>

            {endpoint && (
              <>
                <Typography variant="caption" color={C.muted} sx={{ fontSize: 11.5, display: 'block', mb: '4px', lineHeight: 1.6 }}>
                  {endpoint.description}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 10.5, color: C.dim, fontFamily: 'monospace', display: 'block', mb: '12px' }}>
                  {endpoint.method} {endpoint.path}
                  {endpoint.scope && <span style={{ color: C.muted }}>{'  ·  '}{endpoint.scope}</span>}
                </Typography>

                {endpoint.path_params.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: C.line, mb: '10px' }}>
                      <Typography variant="caption" color={C.muted} sx={{ fontSize: 9.5, letterSpacing: '.08em' }}>PATH</Typography>
                    </Divider>
                    {endpoint.path_params.map((p) => (
                      <TextField
                        key={p.name} fullWidth size="small" sx={{ ...inputSx, mb: '10px' }}
                        label={`${p.name}${p.required ? ' *' : ''}`}
                        helperText={p.help}
                        value={pathParams[p.name] || ''}
                        onChange={(e) => setPathParams((s) => ({ ...s, [p.name]: e.target.value }))}
                      />
                    ))}
                  </>
                )}

                {endpoint.query_params.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: C.line, mb: '10px' }}>
                      <Typography variant="caption" color={C.muted} sx={{ fontSize: 9.5, letterSpacing: '.08em' }}>QUERY</Typography>
                    </Divider>
                    {endpoint.query_params.map((q) => (
                      <TextField
                        key={q.name} fullWidth size="small" sx={{ ...inputSx, mb: '10px' }}
                        label={q.name} helperText={q.help}
                        value={queryParams[q.name] || ''}
                        onChange={(e) => setQueryParams((s) => ({ ...s, [q.name]: e.target.value }))}
                      />
                    ))}
                  </>
                )}

                {endpoint.body_fields.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: C.line, mb: '10px' }}>
                      <Typography variant="caption" color={C.muted} sx={{ fontSize: 9.5, letterSpacing: '.08em' }}>BODY</Typography>
                    </Divider>
                    {endpoint.body_fields.map((f) => (
                      <TextField
                        key={f.name} fullWidth size="small" sx={{ ...inputSx, mb: '10px' }}
                        label={`${f.name}${f.required ? ' *' : ''}`}
                        helperText={f.help}
                        multiline={f.type === 'text' || f.type === 'json'}
                        minRows={f.type === 'text' ? 5 : f.type === 'json' ? 2 : 1}
                        placeholder={f.type === 'json' ? '{ }' : ''}
                        value={bodyFields[f.name] ?? ''}
                        onChange={(e) => setBodyFields((s) => ({ ...s, [f.name]: e.target.value }))}
                      />
                    ))}
                  </>
                )}

                <Button
                  fullWidth variant="contained" disableElevation
                  disabled={sending || !credentials?.configured}
                  onClick={send}
                  startIcon={sending
                    ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                    : <SendOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{ mt: '6px', textTransform: 'none', fontSize: 13, fontWeight: 600 }}
                >
                  {sending ? 'Waiting for the assistant…' : 'Send request'}
                </Button>
              </>
            )}
          </Panel>

          {/* ── Sent / received ──────────────────────────────────────── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>

            <Panel
              title="Sent"
              accent={C.blue}
              icon={<ArrowUpwardOutlinedIcon sx={{ fontSize: 15, color: C.blue }} />}
              right={req && (
                <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>
                  timeout {req.timeout_seconds}s
                </Typography>
              )}
            >
              {!req ? (
                <Typography variant="caption" color={C.muted} sx={{ fontSize: 12 }}>
                  Nothing sent yet.
                </Typography>
              ) : (
                <>
                  <KV k="method" v={req.method} mono color={C.blue} />
                  <KV k="url" v={req.url} mono />
                  {req.scope && <KV k="required scope" v={req.scope} mono />}
                  <Box sx={{ mt: '10px' }}>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, letterSpacing: '.06em' }}>
                      HEADERS
                    </Typography>
                    <Json value={req.headers} max={140} />
                  </Box>
                  {Object.keys(req.query_params || {}).length > 0 && (
                    <Box sx={{ mt: '10px' }}>
                      <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, letterSpacing: '.06em' }}>
                        QUERY
                      </Typography>
                      <Json value={req.query_params} max={120} />
                    </Box>
                  )}
                  <Box sx={{ mt: '10px' }}>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, letterSpacing: '.06em' }}>
                      BODY
                    </Typography>
                    <Json value={req.body === null ? '(no body)' : req.body} max={280} />
                  </Box>
                </>
              )}
            </Panel>

            <Panel
              title="Received"
              accent={statusColor}
              icon={<ArrowDownwardOutlinedIcon sx={{ fontSize: 15, color: statusColor }} />}
              right={result && (
                <>
                  {res && (
                    <Chip label={`HTTP ${res.http_status}`} size="small" sx={{
                      height: 18, fontSize: 10, fontWeight: 700,
                      background: 'transparent', color: statusColor,
                      border: `1px solid ${statusColor}`, '& .MuiChip-label': { px: '6px' },
                    }} />
                  )}
                  <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>
                    {result.elapsed_ms} ms
                  </Typography>
                </>
              )}
            >
              {!result ? (
                <Typography variant="caption" color={C.muted} sx={{ fontSize: 12 }}>
                  Nothing received yet.
                </Typography>
              ) : result.transport_error ? (
                <Alert severity="error" sx={{ fontSize: 12.5 }}>{result.transport_error}</Alert>
              ) : (
                <>
                  {res.error_code && (
                    <Alert severity={res.http_status >= 500 ? 'error' : 'warning'}
                      sx={{ mb: '12px', fontSize: 12.5 }}>
                      <strong>{res.error_code}</strong> — {res.error_message}
                      {res.retry_after && <> · retry after {res.retry_after}s</>}
                    </Alert>
                  )}

                  {/* The two things that drive the conversation loop. */}
                  {execution && (
                    <Box sx={{
                      mb: '12px', p: '10px 12px', borderRadius: '6px',
                      background: '#111827', border: `1px solid ${C.line}`,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px' }}>
                        <Chip label={execution.status} size="small" sx={{
                          height: 18, fontSize: 10, fontWeight: 700,
                          background: 'transparent',
                          color: execution.is_complete ? C.green : C.amber,
                          border: `1px solid ${execution.is_complete ? C.green : C.amber}`,
                          '& .MuiChip-label': { px: '6px' },
                        }} />
                        <Typography variant="caption" color={C.dim} sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                          {execution.execution_id}
                        </Typography>
                        <Tooltip title="Copy execution_id">
                          <IconButton size="small" onClick={() => copy(execution.execution_id)}
                            sx={{ p: '2px', color: C.muted }}>
                            <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {awaiting ? (
                        <>
                          <Typography variant="caption" color={C.amber} sx={{ fontSize: 11, fontWeight: 700 }}>
                            awaiting_input · {awaiting.type}
                          </Typography>
                          <Typography variant="body2" color={C.text} sx={{ fontSize: 12.5, mt: '3px', lineHeight: 1.6 }}>
                            {awaiting.prompt}
                          </Typography>
                          {awaiting.options?.length > 0 && (
                            <Box sx={{ display: 'flex', gap: '6px', mt: '7px', flexWrap: 'wrap' }}>
                              {awaiting.options.map((opt) => (
                                <Chip
                                  key={opt} label={opt} size="small"
                                  onClick={() => {
                                    setEndpointKey('executions.message');
                                    setBodyFields((s) => ({ ...s, message: opt }));
                                  }}
                                  sx={{
                                    height: 22, fontSize: 11, cursor: 'pointer',
                                    background: '#1e293b', color: C.text,
                                    border: `1px solid ${C.border}`,
                                    '&:hover': { borderColor: C.blue },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" color={C.muted} sx={{ fontSize: 11 }}>
                          {execution.is_complete
                            ? 'Complete — no further input needed.'
                            : 'No input awaited (still processing).'}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {replies?.length > 0 && (
                    <Box sx={{ mb: '12px' }}>
                      <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, letterSpacing: '.06em' }}>
                        ASSISTANT REPLIES ({replies.length})
                      </Typography>
                      {replies.map((r, i) => (
                        <Box key={i} sx={{
                          mt: '6px', p: '9px 11px', borderRadius: '6px',
                          background: '#121620', border: `1px solid ${C.line}`,
                        }}>
                          <Typography variant="body2" sx={{
                            fontSize: 12.5, color: C.text, whiteSpace: 'pre-wrap',
                            lineHeight: 1.65, wordBreak: 'break-word',
                          }}>
                            {r.content}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {res.body_truncated && (
                    <Alert severity="info" sx={{ mb: '10px', fontSize: 12 }}>
                      Body truncated for display ({res.body_bytes.toLocaleString()} bytes returned).
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, letterSpacing: '.06em' }}>
                      FULL RESPONSE BODY
                    </Typography>
                    <Tooltip title="Copy response">
                      <IconButton size="small"
                        onClick={() => copy(JSON.stringify(res.json ?? res.text, null, 2))}
                        sx={{ p: '2px', color: C.muted }}>
                        <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, ml: 'auto' }}>
                      {res.body_bytes?.toLocaleString()} bytes
                    </Typography>
                  </Box>
                  <Json value={res.json ?? res.text ?? '(empty)'} max={520} />

                  <Box sx={{ mt: '12px' }}>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, letterSpacing: '.06em' }}>
                      RESPONSE HEADERS
                    </Typography>
                    <Json value={res.headers} max={160} />
                  </Box>
                </>
              )}
            </Panel>

            {history.length > 0 && (
              <Panel
                title="This session"
                icon={<HistoryOutlinedIcon sx={{ fontSize: 15, color: C.muted }} />}
              >
                {history.map((h, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    py: '5px', borderBottom: i < history.length - 1 ? `1px solid ${C.line}` : 'none',
                  }}>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5, width: 62, flexShrink: 0 }}>
                      {h.at.toLocaleTimeString()}
                    </Typography>
                    <Chip label={h.method} size="small" sx={{
                      height: 16, fontSize: 9, fontWeight: 700, width: 52, flexShrink: 0,
                      background: 'transparent', color: C.dim, border: `1px solid ${C.border}`,
                      '& .MuiChip-label': { px: '4px' },
                    }} />
                    <Typography variant="caption" color={C.text} sx={{ fontSize: 11.5, flex: 1, minWidth: 0 }}>
                      {h.label}
                    </Typography>
                    {h.errorCode && (
                      <Typography variant="caption" color={C.red} sx={{ fontSize: 10.5, fontFamily: 'monospace' }}>
                        {h.errorCode}
                      </Typography>
                    )}
                    <Typography variant="caption"
                      sx={{ fontSize: 10.5, color: h.ok ? C.green : C.amber, width: 34, textAlign: 'right' }}>
                      {h.httpStatus ?? '—'}
                    </Typography>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5, width: 58, textAlign: 'right' }}>
                      {h.ms} ms
                    </Typography>
                  </Box>
                ))}
              </Panel>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AiAgentApiPage;
